import { createContext, use, useMemo } from 'react';

import { useRoom } from '@/hooks/useRoom';

import { LIVEKIT_CONFIG_DEFAULTS } from '@/lib/livekit';

import type { LivekitConfig } from '@/types/livekit.type';
import { RoomContext } from '@livekit/components-react';

const SessionContext = createContext<{
  appConfig: LivekitConfig;
  isSessionActive: boolean;
  startSession: (agentId: string, payload: Record<string, string>) => void;
  endSession: () => void;
}>({
  appConfig: LIVEKIT_CONFIG_DEFAULTS,
  isSessionActive: false,
  startSession: () => {},
  endSession: () => {},
});

interface SessionProviderProps {
  appConfig: LivekitConfig;
  children: React.ReactNode;
}

export const SessionProvider = ({ appConfig, children }: SessionProviderProps) => {
  const { room, isSessionActive, startSession, endSession } = useRoom(appConfig);
  const contextValue = useMemo(
    () => ({ appConfig, isSessionActive, startSession, endSession }),
    [appConfig, isSessionActive, startSession, endSession]
  );

  return (
    <RoomContext value={room}>
      <SessionContext value={contextValue}>{children}</SessionContext>
    </RoomContext>
  );
};

export function useSession() {
  return use(SessionContext);
}
